import { TypographyHeading } from "shared/molecules/typography-layout";
import { Nl3LiveclassSchedulesFilter } from "./filter";
import { Box, InputLabel, Paper, Typography, FormControl, Select, MenuItem, Pagination } from "@mui/material";
import Loader from "pages/build-test/loader";
import SectionTable from "shared/organisms/tables/table";
import { useEffect, useState } from "react";
import { GET_LIVECLASS_PACKAGES, GET_PACKAGE_SCHEDULES, GET_VENDOR_PROJECT_USERS } from "utils/graphql/services/live-classes/queries";
import { useLazyQuery, useMutation } from "@apollo/client";
import NoDataFound from "shared/molecules/no-data";
import { Stack } from "@mui/material"
import { table } from "./helper"
import liveClassClientService from "utils/graphql/liveclassClient";
import ContentRight from "shared/molecules/content-right";
import { Link, useHistory } from "react-router-dom";
import { Button } from "@mui/material";
import { DELETE_PACKAGE_SCHEDULE } from "utils/graphql/services/live-classes/mutations";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import { useAppDispatch } from "hooks/redux";
import { utcToIst } from "utils/time-util/utcIstTime";
import CollapsibleTable from "shared/organisms/tables/collapsible-table";
import { useHostLink } from "./host-link";
import { DeleteScheduleDialog } from "./schedule-buttons/delete-schedule-dialog";
import { useCreateSessionAllocation } from "./create-session-allocation";

export default function Nl3PackageSchedules() {

    const [searchPayload, setSearchPayload] = useState({});
    const [page, setPage] = useState(1);
    const [clearValuesTrigger, setClearValuesTrigger] = useState(0);
    const [limit, setLimit] = useState(10);
    const dispatch = useAppDispatch();

    const { handleSessionAllocations, dialogs: sessionAllocationDialogs } = useCreateSessionAllocation();

    const [deletePackageSchedule, { loading: deletingSchedule }] = useMutation(DELETE_PACKAGE_SCHEDULE, {
        client: liveClassClientService,
    });

    // --- Delete confirmation dialog state ---
    const [deleteDialogRow, setDeleteDialogRow] = useState<any>(null);

    const history = useHistory();


    function handleAttendanceSummary(row: any) {
        history.push(`/live-classes/package-schedules/attendance-summary`, { row });
    }

    function handleAllocatedSessions(row: any) {
        history.push(`/live-classes/package-schedules/allocated-sessions`, { row });
    }

    function handleLiveSessions(row: any) {
        history.push(`/live-classes/package-schedules/live-sessions`, { row });
    }

    function handleSessionUsages(row: any) {
        history.push(`/live-classes/package-schedules/session-usages`, { row });
    }

    function handleCoHost(row: any) {
        history.push(`/live-classes/package-schedules/co-host`, { row });
    }
    function handleObserver(row: any) {
        history.push(`/live-classes/package-schedules/observer`, { row });
    }
    function handleEditSchedule(row: any) {
        history.push(`/live-classes/package-schedules/edit`, { row });
    }
    function handleDeleteSchedule(row: any) {
        setDeleteDialogRow(row);
    }

    async function confirmDeleteSchedule() {
        const row = deleteDialogRow;
        try {
            // liveSession carries a projects list now, not a single projectId
            const projectIds = (row?.liveSession?.projects || [])
                .map((p: any) => Number(p?.id))
                .filter((id: number) => Number.isFinite(id));

            const response = await deletePackageSchedule({
                variables: {
                    input: {
                        packageScheduleId: Number(row?.id),
                        projectIds,
                        vendorUserId: Number(row.vendorUserId)
                    },
                },
            });
            dispatch(
                snackbarRequest({
                    message: response?.data?.deletePackageSchedule?.message || "Package Schedule deleted successfully",
                })
            );
            Nl3LiveclassSchedulesList({
                variables: { input: { limit, offset: (page - 1) * limit, ...searchPayload } },
                fetchPolicy: "network-only",
            });
        } catch (err: any) {
            dispatch(
                snackbarRequest({
                    message: err?.graphQLErrors?.[0]?.message || "Failed to delete Package Schedule",
                    type: "error",
                })
            );
        } finally {
            setDeleteDialogRow(null);
        }
    }
    const { handleScheduleCopy } = useHostLink();

    const [Nl3LiveclassSchedulesList, { loading, error, data }] = useLazyQuery(
        GET_PACKAGE_SCHEDULES,
        {
            client: liveClassClientService,
        },
    );

    useEffect(() => {
        Nl3LiveclassSchedulesList({
            variables: {
                input: {
                    limit: limit,
                    offset: (page - 1) * limit,
                    ...searchPayload,
                },
            },
        });
    }, [page, limit, searchPayload]);

    const nl3LiveclassSchedulesData = (
        data?.getPackageSchedules?.packageSchedules || []
    ).map((t: any) => ({
        ...t,
        packageName: t?.liveclassPackages
        ?.map((pkg: any) => `${pkg?.name} - ${pkg?.isActive ? "Active" : "Inactive"}`)
        .join(", "),
        scheduledStartTime: utcToIst(t?.liveSession?.scheduledStartTime),
        scheduledEndTime: utcToIst(t?.liveSession?.scheduledEndTime),
        createdAt: utcToIst(t.createdAt),
        updatedAt: utcToIst(t.updatedAt),

    }));

    return (
        <>
            {sessionAllocationDialogs}

            <DeleteScheduleDialog
                open={Boolean(deleteDialogRow)}
                row={deleteDialogRow}
                deleting={deletingSchedule}
                onClose={() => setDeleteDialogRow(null)}
                onConfirm={confirmDeleteSchedule}
            />

            <ContentRight>
                <Link to="/live-classes/package-schedules/create">
                    <Button variant="contained">Create Package Schedule</Button>
                </Link>
            </ContentRight>

            <TypographyHeading sx={{ mb: 2, mt: 3, fontSize: "25px" }}>
                Package Schedules{" "}
            </TypographyHeading>

            <Nl3LiveclassSchedulesFilter
                setSearchPayload={setSearchPayload}
                setPage={setPage}
                clearValuesTrigger={clearValuesTrigger}
            />

            <Paper elevation={2} sx={{ p: 2, mt: 3 }}>

                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <FormControl size="small" sx={{ width: 140 }}>
                        <InputLabel>Show Entries</InputLabel>
                        <Select
                            value={limit}
                            label="Show Entries"
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPage(1); // reset to first page
                            }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={500}>500</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <div>
                    {loading && <Loader />}
                    {!loading && data?.getPackageSchedules?.packageSchedules?.length > 0 ? (
                        <CollapsibleTable
                            data={nl3LiveclassSchedulesData}
                            table={table}
                            id="id"
                            name="id"
                            rowsPerPageCount={limit}
                            style="false"
                            totalRecords={data?.getPackageSchedules?.totalCount || 0}
                            paginationSegmentApi="false"
                            viewSecondaryData="false"
                            showPaginationFooter={false}
                            handleLiveSessions={handleLiveSessions}
                            handleSessionAllocations={handleSessionAllocations}
                            handleSessionUsages={handleSessionUsages}
                            handleAllocatedSessions={handleAllocatedSessions}
                            handleScheduleCopy={handleScheduleCopy}
                            handleCoHost={handleCoHost}
                            handleObserver={handleObserver}
                            handleAttendanceSummary={handleAttendanceSummary}
                            handleEdit={handleEditSchedule}
                            handleDelete={handleDeleteSchedule}
                            disableInactiveRows
                        />
                    ) : (
                        <div>{!loading && <NoDataFound message="No Data Found" />}</div>
                    )}
                </div>

                <Box sx={{ marginTop: "20px" }}>
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Pagination
                            count={Math.ceil(data?.getPackageSchedules?.totalCount / limit) || 1}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                            size="medium"
                        />
                    </Stack>
                </Box>

            </Paper>
        </>
    )
}